// @greenlight-spec: 12-azure-installation.md
// @greenlight-spec: 28-it-dashboard-ui.md

import * as React from 'react';
import { CheckCircle2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { StepHeader } from '@/components/install/StepHeader';
import { IdpCheckList } from '@/components/greenlight/idp/IdpCheckList';
import { IdpProviderFields, IdpProviderPicker } from '@/components/greenlight/idp/IdpProviderForm';
import {
  joinHostedDomains,
  splitHostedDomains,
  useIdpForm,
  type IdpFormInitial,
} from '@/components/greenlight/idp/useIdpForm';
import { api } from '@/lib/api';
import { errorBodyMessage, thrownMessage } from '@/lib/apiErrors';
import {
  parseIdpTestSignIn,
  type IdpTestSignInOutcome,
  type IdpTestSignInPurpose,
  type InstallationState,
  type RoleSource,
} from '@greenlight/shared';
import type { IdpValidationResult } from '@greenlight/contract';
import {
  describeIdpTestFailure,
  idpTestNoReturnHints,
  idpTestSignInHref,
} from '@/components/install/steps/idpTestSignIn';
import {
  getIdpProvider,
  setupStepHref,
  setupStepText,
  type IdpProviderId,
} from '@/lib/idpProviders';

/** Details persisted on the completed `idp` step (drives the resumable summary). */
interface IdpDetails {
  provider?: IdpProviderId;
  issuer?: string;
  client_id?: string;
  /** Generic OIDC only: the sign-in button label. */
  display_name?: string;
  /**
   * Where platform roles come from (docs/10 §IdP group → platform role
   * mapping). `idp` maps groups to roles; `greenlight` names an initial admin
   * by email and assigns roles in-product. Google is always `greenlight`.
   */
  role_source?: RoleSource;
  group_claim?: string;
  /**
   * Entra/Okta group→role mapping; the wizard's admin-group field writes
   * `org_admin`. Google carries none — its roles are managed in the dashboard.
   */
  role_groups?: { org_admin?: string[]; admin?: string[]; user?: string[] };
  /** Legacy single-field form written by earlier wizard versions; read-only. */
  admin_group_id?: string;
  /** Google only; empty means no Greenlight-side domain restriction. */
  hosted_domains?: string[];
  /** Greenlight-managed roles only: the org admin pre-created at save. */
  initial_admin_email?: string;
  /**
   * The installer's test sign-ins through the saved configuration, written by
   * the login callback; the step completes only on an administrator pass.
   */
  test_sign_in?: Record<string, unknown>;
}

/** Loose email check for the Greenlight-managed initial-admin field. */
function looksLikeEmail(value: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.trim());
}

/**
 * Whether a Google Workspace-domain list would refuse the administrator the
 * install is naming. An empty list restricts nothing and is a deliberate
 * delegation to the OAuth client's user type, so it warns about nothing.
 */
function hostedDomainsExcludeAdmin(hostedDomains: string, adminEmail: string): boolean {
  const domains = splitHostedDomains(hostedDomains);
  const adminDomain = adminEmail.trim().toLowerCase().split('@')[1];
  if (domains.length === 0 || !adminDomain) return false;
  return !domains.includes(adminDomain);
}

/** The saved initial admin, else empty: the wizard has no other identity to offer. */
function prefilledAdminEmail(configured: IdpDetails | undefined): string {
  return configured?.initial_admin_email ?? '';
}

/** The role source to prefill from a resumed config (Google is always greenlight). */
function configuredRoleSource(details: IdpDetails | undefined): RoleSource {
  if (details?.provider === 'google') return 'greenlight';
  if (details?.role_source) return details.role_source;
  // Back-compat with a pre-role_source persisted step: infer from the mapping.
  const rg = details?.role_groups;
  const hasMapping =
    (rg?.org_admin?.length ?? 0) > 0 ||
    (rg?.admin?.length ?? 0) > 0 ||
    (rg?.user?.length ?? 0) > 0 ||
    Boolean(details?.admin_group_id);
  return hasMapping ? 'idp' : 'greenlight';
}

/** A comparable form of the step's editable values, to tell saved from unsaved. */
function formSnapshot(values: {
  provider: string | null;
  issuerUrl: string;
  clientId: string;
  displayName: string;
  roleSource: RoleSource;
  groupClaim: string;
  adminGroupId: string;
  itAdminGroupId: string;
  userGroupId: string;
  hostedDomains: string;
  initialAdminEmail: string;
}): string {
  const greenlight = values.roleSource === 'greenlight';
  return JSON.stringify({
    provider: values.provider,
    issuer: values.issuerUrl.trim().replace(/\/+$/, ''),
    clientId: values.clientId.trim(),
    displayName: values.provider === 'oidc' ? values.displayName.trim() : '',
    roleSource: values.roleSource,
    groupClaim: greenlight ? '' : values.groupClaim.trim(),
    adminGroupId: greenlight ? '' : values.adminGroupId.trim(),
    itAdminGroupId: greenlight ? '' : values.itAdminGroupId.trim(),
    userGroupId: values.userGroupId.trim(),
    hostedDomains: values.provider === 'google' ? splitHostedDomains(values.hostedDomains) : [],
    initialAdminEmail: greenlight ? values.initialAdminEmail.trim().toLowerCase() : '',
  });
}

/** The admin group shown on resume — `role_groups.org_admin`, or the legacy field. */
function configuredAdminGroup(details: IdpDetails | undefined): string {
  return details?.role_groups?.org_admin?.[0] ?? details?.admin_group_id ?? '';
}

interface IdpStepProps {
  /** Control-plane base URL; OIDC redirect URIs derive from it. */
  greenlightBaseUrl: string;
  installationState: InstallationState;
  blocked: boolean;
  /** Advance past the completed step without re-saving. */
  onContinue: () => void;
  /** Refetch install status; a save never advances, since the step waits on the test sign-in. */
  onRefreshStatus?: () => Promise<void> | void;
}

/** Fast client-side pre-check so an obviously malformed issuer fails without a
 * round trip; the server probe is the real validation. */
function isPlausibleIssuer(issuer: string): boolean {
  try {
    const url = new URL(issuer.trim());
    return url.protocol === 'https:' && url.hostname.includes('.');
  } catch {
    return false;
  }
}

export function IdpStep({
  greenlightBaseUrl,
  installationState,
  blocked,
  onContinue,
  onRefreshStatus,
}: IdpStepProps) {
  const configured = installationState.steps?.idp?.details as IdpDetails | undefined;
  const hasConfig = Boolean(configured?.provider);

  const adminEmailPrefill = prefilledAdminEmail(configured);
  const [initialAdminEmail, setInitialAdminEmail] = React.useState(adminEmailPrefill);

  const validateMutation = api.install.validateIdp.useMutation();
  const configureMutation = api.install.configureIdp.useMutation();

  const [checks, setChecks] = React.useState<IdpValidationResult | null>(null);
  const [validateError, setValidateError] = React.useState<string | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  // A test runs in its own tab and its result lands on the server, so while one
  // is under way this tab polls for it and keeps the clock that turns the
  // waiting notice into the no-return hints.
  const storedTests = parseIdpTestSignIn(installationState.steps?.idp?.details);
  const [now, setNow] = React.useState(() => Date.now());
  // A test link can open its tab in the background, where this tab never blurs
  // and so never refetches; the click itself marks the test under way until
  // the server's record for it changes or the test reads as never returning.
  const [clicked, setClicked] = React.useState<{
    purpose: IdpTestSignInPurpose;
    at: number;
    storedAt: string | undefined;
  } | null>(null);
  const clickedPending =
    clicked !== null &&
    now - clicked.at < IDP_TEST_STALL_MS &&
    storedTests[clicked.purpose]?.at === clicked.storedAt;
  const recordedTests = clickedPending
    ? {
        ...storedTests,
        [clicked.purpose]: { status: 'started' as const, at: new Date(clicked.at).toISOString() },
      }
    : storedTests;
  const startTest = (purpose: IdpTestSignInPurpose): void => {
    setNow(Date.now());
    setClicked({ purpose, at: Date.now(), storedAt: storedTests[purpose]?.at });
  };
  const testRunning =
    recordedTests.admin?.status === 'started' || recordedTests.user?.status === 'started';
  React.useEffect(() => {
    if (!testRunning) return;
    const id = window.setInterval(() => {
      setNow(Date.now());
      if (document.visibilityState === 'visible') void onRefreshStatus?.();
    }, IDP_TEST_POLL_MS);
    return () => window.clearInterval(id);
  }, [testRunning, onRefreshStatus]);

  // Monotonic probe sequence: a response that lands after the issuer or
  // provider changed must not mark the *old* issuer as validated while the
  // form shows a new one.
  const probeSeq = React.useRef(0);

  const resetProbe = (): void => {
    probeSeq.current += 1;
    setChecks(null);
    setValidateError(null);
    setSaveError(null);
  };

  const initial: IdpFormInitial = {
    provider: configured?.provider ?? null,
    issuerUrl: configured?.issuer ?? '',
    clientId: configured?.client_id ?? '',
    displayName: configured?.display_name ?? '',
    roleSource: configuredRoleSource(configured),
    groupClaim: configured?.group_claim ?? 'groups',
    adminGroupId: configuredAdminGroup(configured),
    itAdminGroupId: configured?.role_groups?.admin?.[0] ?? '',
    userGroupId: configured?.role_groups?.user?.[0] ?? '',
    hostedDomains: joinHostedDomains(configured?.hosted_domains),
    roleGroups: configured?.role_groups,
  };

  const form = useIdpForm({
    initial,
    // The wizard configures a fresh install, so the client secret is always
    // required; Settings keeps the stored one on a same-provider edit.
    secretRequired: () => true,
    reselectIsNoop: true,
    onInvalidateValidation: resetProbe,
  });

  const provider = form.providerDef;
  const base = greenlightBaseUrl.replace(/\/$/, '');
  const redirectUri = `${base}/auth/callback`;
  const signOutUri = `${base}/auth/login`;

  const handleValidate = async (): Promise<void> => {
    const seq = ++probeSeq.current;
    setChecks(null);
    setValidateError(null);
    setSaveError(null);

    if (!isPlausibleIssuer(form.issuerUrl)) {
      setValidateError('Confirm the issuer URL copied from your IdP is a valid https:// address.');
      return;
    }

    try {
      const res = await validateMutation.mutateAsync({
        body: {
          issuer_url: form.issuerUrl.trim(),
          ...(form.provider ? { provider: form.provider } : {}),
          client_id: form.clientId.trim(),
          client_secret: form.clientSecret,
        },
      });
      if (seq !== probeSeq.current) return; // stale response — form changed since
      if (res.status === 200) {
        // A failing check carries its own remediation on its row, so the alert
        // below the list only covers failures that produced no result at all.
        setChecks(res.body);
      } else {
        setValidateError(errorBodyMessage(res.body, 'Validation failed.'));
      }
    } catch (err) {
      if (seq !== probeSeq.current) return;
      setValidateError(
        thrownMessage(err, 'Could not reach the control plane. Check it is running and try again.'),
      );
    }
  };

  // The probe exercises the issuer, the client ID and the secret together, so
  // it needs all three — validating a half-filled form is not possible.
  const canValidate =
    form.issuerUrl.trim() !== '' && form.clientId.trim() !== '' && form.clientSecret.trim() !== '';
  // `ok` is "nothing failed", not "everything passed": an inconclusive check —
  // the normal Google outcome — leaves the configuration saveable.
  const validated = checks?.ok === true;
  const canSave =
    validated &&
    form.fieldsComplete &&
    (form.effectiveRoleSource === 'greenlight' ? looksLikeEmail(initialAdminEmail) : true);

  const handleSave = async (): Promise<void> => {
    if (!form.provider) return;
    setSaveError(null);
    try {
      const body = {
        ...form.buildBody(),
        // The wizard always requires a secret (fresh install), so it is always
        // present here — restate it to satisfy the configureIdp contract, whose
        // client_secret is required unlike the shared body's optional field.
        client_secret: form.clientSecret,
        ...(form.effectiveRoleSource === 'greenlight' && initialAdminEmail.trim()
          ? { initial_admin_email: initialAdminEmail.trim() }
          : {}),
      };
      const res = await configureMutation.mutateAsync({ body });
      if (res.status === 200) {
        // The step stays on screen: it completes only when the administrator's
        // test sign-in through the saved configuration passes. The secret is
        // never shown again, so the field empties once it is stored.
        form.setClientSecret('');
        await onRefreshStatus?.();
      } else {
        setSaveError(errorBodyMessage(res.body, 'Could not save the configuration.'));
      }
    } catch (err) {
      setSaveError(
        thrownMessage(
          err,
          'Could not save the configuration. Check the control plane and try again.',
        ),
      );
    }
  };

  const isSaving = configureMutation.isPending;

  // The form is the only screen: it holds what was saved, and an edit marks it
  // unsaved, which hides the test until the change is saved.
  const savedSnapshot = hasConfig
    ? formSnapshot({
        provider: initial.provider,
        issuerUrl: initial.issuerUrl,
        clientId: initial.clientId,
        displayName: initial.displayName,
        roleSource: initial.roleSource,
        groupClaim: initial.groupClaim,
        adminGroupId: initial.adminGroupId,
        itAdminGroupId: initial.itAdminGroupId,
        userGroupId: initial.userGroupId,
        hostedDomains: initial.hostedDomains,
        initialAdminEmail: configured?.initial_admin_email ?? '',
      })
    : null;
  const currentSnapshot = formSnapshot({
    provider: form.provider,
    issuerUrl: form.issuerUrl,
    clientId: form.clientId,
    displayName: form.displayName,
    roleSource: form.effectiveRoleSource,
    groupClaim: form.groupClaim,
    adminGroupId: form.adminGroupId,
    itAdminGroupId: form.itAdminGroupId,
    userGroupId: form.userGroupId,
    hostedDomains: form.hostedDomains,
    initialAdminEmail,
  });
  const unsaved =
    !hasConfig || savedSnapshot !== currentSnapshot || form.clientSecret.trim() !== '';
  const tests = recordedTests;
  // Both tests share the login's state cookie, so a second one started while
  // the first is still at the provider would void it; the actions return once
  // the running test reports or reads as never coming back.
  const testInFlight = [tests.admin, tests.user].some(
    (t) => t?.status === 'started' && now - Date.parse(t.at) < IDP_TEST_STALL_MS,
  );
  const configuredProvider = getIdpProvider(configured?.provider);
  const groupClaim = configured?.group_claim ?? 'groups';
  // Matches the server's Finish rule: a complete step with a recorded pass.
  const adminPassed =
    installationState.steps?.idp?.status === 'complete' && tests.admin?.status === 'passed';
  const failureContext = { provider: configured?.provider, groupClaim, redirectUri };

  const Header = (
    <StepHeader step="idp" title="Connect your identity provider">
      Greenlight uses your IdP for single sign-on. Configure one provider now; you can change it
      later under Settings → Identity.
    </StepHeader>
  );

  if (blocked) {
    return (
      <div className="space-y-6" data-testid="step-idp">
        {Header}
        <div
          className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground"
          data-testid="step-blocked"
        >
          This step is blocked until the verified-URL handoff is complete, because OIDC redirect
          URIs must use the final HTTPS control-plane URL.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="step-idp">
      {Header}

      {hasConfig && unsaved && (
        <Alert data-testid="idp-replace-warning">
          <AlertTitle>Saving replaces the current configuration</AlertTitle>
          <AlertDescription>
            Saving a new provider replaces the existing IdP and invalidates all active sessions.
            Everyone signs in again through the new provider.
          </AlertDescription>
        </Alert>
      )}

      {/* Provider selection — one decision per screen on narrow widths. A switch
          also resets the initial-admin email to its prefill. */}
      <IdpProviderPicker
        providerId={form.provider}
        legend="Choose a provider"
        onSelect={(id) => {
          if (id !== form.provider) setInitialAdminEmail(adminEmailPrefill);
          form.selectProvider(id);
        }}
      />

      {provider && (
        <div className="space-y-6" data-testid="idp-config-shell">
          {/* Redirect URIs to register in the IdP console. */}
          <div className="space-y-3">
            <h2 className="text-base font-semibold tracking-tight">
              Register these URIs in {provider.label}
            </h2>
            <dl className="divide-y divide-border rounded-lg border text-sm">
              <SummaryRow label="Redirect URI" value={redirectUri} />
              <SummaryRow label="Sign-out redirect" value={signOutUri} />
            </dl>
          </div>

          {/* Inline checklist + link to the per-provider guide. */}
          <div className="space-y-2">
            <h2 className="text-base font-semibold tracking-tight">Set up {provider.label}</h2>
            <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
              {provider.setupSteps(form.effectiveRoleSource).map((step) => {
                const text = setupStepText(step);
                const href = setupStepHref(step);
                return (
                  <li key={text}>
                    {href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${text} (opens in new tab)`}
                        className="text-link underline"
                      >
                        {text}
                      </a>
                    ) : (
                      text
                    )}
                  </li>
                );
              })}
            </ol>
            <a
              href={provider.docsUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-block text-sm text-link underline"
              data-testid="idp-docs-link"
            >
              {provider.label} setup guide
            </a>
          </div>

          <IdpProviderFields
            form={form}
            redirectUri={redirectUri}
            greenlightBlurb="Name an initial org admin; assign roles in the dashboard afterward."
            {...(hasConfig
              ? { secretHint: 'Saved. Enter it again only to change the configuration.' }
              : {})}
            googleRolesNote="Google Workspace carries no group claim, so roles are managed in the dashboard. Name the first org admin below; assign the rest under Users after sign-in."
            hostedDomainsExtra={
              hostedDomainsExcludeAdmin(form.hostedDomains, initialAdminEmail) ? (
                <Alert data-testid="idp-hosted-domain-warning">
                  <AlertDescription>
                    None of these domains matches{' '}
                    <span className="font-mono">{initialAdminEmail.trim()}</span>, the administrator
                    you are naming below. Saved as-is, Google sign-in refuses that account and the
                    org has no usable administrator. Add their domain, or clear the list.
                  </AlertDescription>
                </Alert>
              ) : null
            }
            greenlightExtra={
              <div className="space-y-2">
                <Label htmlFor="idp-initial-admin">Initial org admin email</Label>
                <Input
                  id="idp-initial-admin"
                  type="email"
                  value={initialAdminEmail}
                  placeholder="it-admin@acme.com"
                  onChange={(e) => setInitialAdminEmail(e.target.value)}
                  className="h-11 md:h-9"
                  data-testid="idp-initial-admin-email"
                />
                <p className="text-xs text-muted-foreground" data-testid="idp-initial-admin-hint">
                  This person becomes the first org admin, and it is the account you sign in to
                  Greenlight with when setup finishes. Enter the email exactly as your identity
                  provider sends it: their first sign-in is matched by email. Double-check it — a
                  typo leaves the org without an administrator.
                </p>
              </div>
            }
          />

          {/* Validate: verify the issuer and the client registration together.
              The button needs a complete form because it now exercises all
              three fields, not the issuer alone. */}
          <div className="space-y-3">
            <Button
              type="button"
              variant="outline"
              className="w-full md:w-auto h-11 md:h-9"
              onClick={() => void handleValidate()}
              disabled={!canValidate || validateMutation.isPending}
              data-testid="idp-validate"
            >
              {validateMutation.isPending ? 'Validating…' : 'Validate configuration'}
            </Button>

            <IdpCheckList result={checks} running={validateMutation.isPending} />

            {validateError && (
              <Alert variant="destructive" data-testid="idp-validate-error">
                <AlertDescription>{validateError}</AlertDescription>
              </Alert>
            )}
          </div>

          {saveError && (
            <Alert variant="destructive" data-testid="idp-save-error">
              <AlertDescription>{saveError}</AlertDescription>
            </Alert>
          )}

          <Button
            type="button"
            className="w-full md:w-auto h-11 md:h-9"
            onClick={() => void handleSave()}
            disabled={!canSave || isSaving || !unsaved}
            data-testid="idp-save"
          >
            {isSaving ? 'Saving…' : unsaved ? 'Save' : 'Saved'}
          </Button>
        </div>
      )}

      {hasConfig && (
        <>
          <section className="space-y-3" data-testid="idp-test-signin">
            <h2 className="text-base font-semibold tracking-tight">
              {adminPassed && !unsaved
                ? 'Sign-in works'
                : 'Sign in as the org admin to finish this step'}
            </h2>
            <p className="text-sm text-muted-foreground">
              Greenlight opens {configuredProvider?.label ?? 'your identity provider'} in a new tab
              and checks that the account would sign in as an org admin. The check creates no
              session and changes nothing; this step shows the result.
            </p>
            {unsaved ? (
              <p className="text-sm text-muted-foreground" data-testid="idp-test-signin-unsaved">
                You have unsaved changes. Save them, then test the sign-in.
              </p>
            ) : (
              <>
                <TestOutcome
                  purpose="admin"
                  outcome={tests.admin}
                  context={failureContext}
                  providerLabel={configuredProvider?.label}
                  now={now}
                />
                {!testInFlight && (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      asChild
                      variant={adminPassed ? 'outline' : 'default'}
                      className="w-full sm:w-auto h-11 md:h-9"
                    >
                      <a
                        href={idpTestSignInHref(greenlightBaseUrl, 'admin')}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="idp-test-login"
                        onClick={() => startTest('admin')}
                        onAuxClick={() => startTest('admin')}
                      >
                        {adminPassed
                          ? 'Test the org admin sign-in again'
                          : 'Test sign-in as the org admin'}
                      </a>
                    </Button>
                    <Button asChild variant="ghost" className="w-full sm:w-auto h-11 md:h-9">
                      <a
                        href={idpTestSignInHref(greenlightBaseUrl, 'user')}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="idp-test-login-user"
                        onClick={() => startTest('user')}
                        onAuxClick={() => startTest('user')}
                      >
                        Also test a regular user (optional)
                      </a>
                    </Button>
                  </div>
                )}
                <TestOutcome
                  purpose="user"
                  outcome={tests.user}
                  context={failureContext}
                  providerLabel={configuredProvider?.label}
                  now={now}
                />
              </>
            )}
          </section>

          <Button
            type="button"
            className="w-full sm:w-auto h-11 md:h-9"
            onClick={onContinue}
            disabled={!adminPassed || unsaved}
            data-testid="step-next"
          >
            Continue
          </Button>
        </>
      )}
    </div>
  );
}

const IDP_TEST_POLL_MS = 3_000;
/** How long a started test reads as under way before the no-return hints show. */
const IDP_TEST_STALL_MS = 60_000;

/** One recorded test: nothing yet, under way, started and never finished, passed, or failed. */
function TestOutcome({
  purpose,
  outcome,
  context,
  providerLabel,
  now,
}: {
  purpose: IdpTestSignInPurpose;
  outcome: IdpTestSignInOutcome | undefined;
  context: { provider: IdpProviderId | undefined; groupClaim: string; redirectUri: string };
  providerLabel: string | undefined;
  now: number;
}) {
  if (!outcome) return null;
  const when = new Date(outcome.at).toLocaleString();
  if (outcome.status === 'passed') {
    return (
      <p
        className="flex items-start gap-2 text-sm"
        data-testid={`idp-test-signin-passed-${purpose}`}
      >
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gl-green-text" aria-hidden="true" />
        <span>
          <span className="font-medium text-gl-green-text">
            {outcome.email ?? 'The account'}{' '}
            {purpose === 'admin'
              ? 'signed in and would be an org admin.'
              : `signed in and would be a ${outcome.role ?? 'user'}.`}
          </span>{' '}
          <span className="text-muted-foreground">Tested {when}.</span>
        </span>
      </p>
    );
  }
  if (outcome.status === 'started' && now - Date.parse(outcome.at) < IDP_TEST_STALL_MS) {
    return (
      <p
        className="text-sm text-muted-foreground"
        data-testid={`idp-test-signin-waiting-${purpose}`}
      >
        Waiting for the sign-in in the other tab. This step updates when it finishes.
      </p>
    );
  }
  if (outcome.status === 'started') {
    const hints = idpTestNoReturnHints(context.provider, context.redirectUri);
    return (
      <Alert data-testid={`idp-test-signin-stale-${purpose}`}>
        <AlertTitle>The test started {when} did not come back</AlertTitle>
        <AlertDescription className="space-y-2">
          <p>
            If {providerLabel ?? 'your identity provider'} showed an error in the other tab instead
            of finishing the sign-in, the usual causes are:
          </p>
          <ul className="list-disc space-y-1 pl-5">
            {hints.map((h) => (
              <li key={h.code ?? h.cause}>
                {h.code ? <span className="font-mono">{h.code}</span> : null}
                {h.code ? ' — ' : ''}
                {h.cause} {h.fix}
              </li>
            ))}
          </ul>
        </AlertDescription>
      </Alert>
    );
  }
  const view = describeIdpTestFailure(outcome, { purpose, ...context });
  return (
    <Alert variant="destructive" data-testid={`idp-test-signin-failed-${purpose}`}>
      <AlertTitle>{view.title}</AlertTitle>
      <AlertDescription className="space-y-2">
        <p>{view.detail}</p>
        {view.comparison.length > 0 && (
          <dl className="divide-y divide-border rounded-md border text-sm">
            {view.comparison.map((row) => (
              <div key={row.label} className="flex flex-col sm:flex-row gap-1 px-3 py-2">
                <dt className="w-full sm:w-56 shrink-0 text-muted-foreground">{row.label}</dt>
                <dd className="font-mono break-all">{row.value}</dd>
              </div>
            ))}
          </dl>
        )}
        {view.fix && <p>{view.fix}</p>}
        <p className="text-muted-foreground">Tested {when}.</p>
      </AlertDescription>
    </Alert>
  );
}

function SummaryRow({
  label,
  value,
  mono = true,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  if (!value) return null;
  return (
    <div className="flex flex-col sm:flex-row sm:items-center px-4 py-3 gap-1">
      <dt className="text-muted-foreground w-full sm:w-40 shrink-0">{label}</dt>
      <dd className="font-medium min-w-0">
        {mono ? (
          <MonoValue copyable wrap>
            {value}
          </MonoValue>
        ) : (
          <span className="text-muted-foreground font-normal">{value}</span>
        )}
      </dd>
    </div>
  );
}
